import { useEffect, useMemo, useState } from 'react';
import { ButtonField, ButtonFieldIcon, Iconsvg, SearchField, Tooltip, TooltipBody } from '@wk/components-react16';
import { API_URL, API_USER } from '../../config/constants';
import HttpService from '../../config/http.service';
import { Toast } from '../../services';
import AddTaxonomy from './model/AddTaxonomy';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import DeleteConfirm from '../../components/DeleteConfirm';
import { Privilege } from '../../config/constants/privilege';
import { hideLoading, showLoading } from '../../store/authReducer';
import moment from 'moment';

const TaxonomyTab = () => {
    const dispatch = useDispatch();
    const { authUser, authProject } = useSelector((state: RootState) => state.auth);
    const [projectList, setProjectList] = useState([] as any);
    const [taxonomyList, setTaxonomyList] = useState([] as any);
    const [isLoad, setIsLoad] = useState(false);
    const [selectedItem, setSelectedItem] = useState({} as any);
    const [isOpenAddEdit, setIsOpenAddEdit] = useState(false);
    const [isOpenDeleteConfirm, setIsOpenDeleteConfirm] = useState(false);
    const [isEditPrivilege, setIsEditPrivilege] = useState(false);
    const [isDeletePrivilege, setIsDeletePrivilege] = useState(false);
    const [searchText, setSearchText] = useState('' as string);

    const addEdit = (item: any) => {
        setSelectedItem(item);
        setIsOpenAddEdit(true);
    }

    const handleSearchChange = (value: string) => {
        setSearchText(value);
    };

    const getTaxonomy = () => {
        dispatch(showLoading());
        HttpService.getRequest(`${API_URL.GET_TAXONOMY_BY_PROJECT_ID}/${authProject.id}`).then(
        (res: any) => {
            dispatch(hideLoading());
            setTaxonomyList(res? res : []);
        },
        (err: any) => {
            dispatch(hideLoading());
            Toast(err.message, "error");
        })
    }

    const getProjects = () => {
        HttpService.getRequest(API_URL.GET_PROJECT).then(
        (res: any) => {
            const list = res.map((item: any) => ({ ...item, label: item.name, taxonomyType: item.taxonomyType, isSelected: false }));
            setProjectList(list);
        },
        (err: any) => {
            Toast(err.message, "error")
        },
        )
    }

    const handleDelete = (item: any) => {
        setIsOpenDeleteConfirm(true);
        setSelectedItem(item);
    }

    const deleteItem = () => {
        HttpService.deleteRequest(`${API_URL.DELETE_TAXONOMY}/${selectedItem.id}`).then(
            (res: any) => {
                Toast("Taxonomy deleted successfully.", "success");
                setIsLoad(true);
                setIsOpenDeleteConfirm(false);
                setSelectedItem({});
            },
            (err: any) => {
                setIsOpenDeleteConfirm(false);
                setSelectedItem({});
                Toast(err.message, "error");
            }
        )
    }

    useEffect(() => {
        if (isLoad) {
            getTaxonomy();
            setIsLoad(false);
        }
    }, [isLoad]);

    useEffect(() => {
        if (authProject && authProject.id) {
            getProjects();
            getTaxonomy();
        }
    }, []);

    useEffect(() => {
        if (authUser && authUser.privileges) {
            setIsEditPrivilege(authUser.privileges.includes(Privilege[Privilege.EDIT_TAXONOMY]));
            setIsDeletePrivilege(authUser.privileges.includes(Privilege[Privilege.DELETE_TAXONOMY]));
        } else {
            setIsEditPrivilege(false);
            setIsDeletePrivilege(false);
        }
    }, [authUser]);

    const filterTree = (nodes: any[], term: string): any[] =>
        nodes.map(node => {
            const isMatch = node?.name?.toLowerCase().includes(term.toLowerCase());
            if (isMatch) {
                return { ...node };
            }
            const children = node.children ? filterTree(node.children, term) : [];
            if (children.length > 0) {
                return { ...node, children: children };
            }
            return null;
        }).filter(Boolean);

    const filteredTaxonomyList = useMemo(() => {
        if (!searchText.trim()) return taxonomyList;

        return taxonomyList.filter((item: any) =>
            item.name.toLowerCase().includes(searchText.toLowerCase())
        );
    }, [searchText, taxonomyList]);

    const exportTaxonomy = () => {
        dispatch(showLoading());
        HttpService.getRequestBlob(`${API_USER.EXPORT_TAXONOMY}/${authProject.id}`).then(
            (res: any) => {
                const url = window.URL.createObjectURL(res);
                
                const link = document.createElement('a');
                link.href = url;
                link.download = `Taxonomy_${moment().format('YYYY-MM-DD_HHmmss')}.xlsx`;
                document.body.appendChild(link);
                link.click();
                
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                
                Toast('Taxonomy exported successfully', 'success');
                dispatch(hideLoading());
            },
            (err: any) => {
                Toast(err.message, "error");
                dispatch(hideLoading());
            },
        )
    };
    return (
        <div className="p-20 wk-card-container mb-0 rounded-3 box-shadow">
            
            {isEditPrivilege && (
                <div className="wk-row">
                    <div className="wk-col-6">
                        <SearchField size="small" label="" labelFor="search-taxonomy-1">
                            <input
                                type="search"
                                id="search-taxonomy-1"
                                name="searchTaxonomy"
                                value={searchText}
                                placeholder="Search Taxonomy Name"
                                onChange={(event: any) => handleSearchChange(event.target.value)}
                            />
                        </SearchField>
                    </div>
                    <div className="wk-col-6 text-right">
                        <span className='mr-10'>
                            <ButtonFieldIcon>
                                <button id='export-taxonomy' type="button" onClick={() => exportTaxonomy()}>
                                    <Iconsvg name="export" color='#020102ff'></Iconsvg>
                                </button>
                            </ButtonFieldIcon>
                            <Tooltip
                                targetSelector="#export-taxonomy"
                                identifier="tooltip-top"
                                position="top"
                                >
                                <TooltipBody slot="tooltipBody">Export Taxonomy Library</TooltipBody>
                            </Tooltip>
                        </span>
                        <ButtonField size={'small'} mode={'ghost'}>
                            <button type="button" className="mr-5" onClick={() => addEdit({ projectId: authProject.id })}>
                                Add Taxonomy
                            </button>
                        </ButtonField>
                    </div>
                </div>
            )}
            <div className="wk-row mt-10">
                <div className="wk-col-12">
                    <table className="wk-table wk-table-bordered wk-table-small">
                        <thead>
                            <tr>
                                <th>Dimensions</th>
                                <th>Parent Hierarchy</th>
                                <th>Taxonomy Name</th>
                                <th>Code</th>
                                {(isEditPrivilege || isDeletePrivilege) && (<th style={{ width: '80px' }}>Actions</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTaxonomyList.length > 0 ? filteredTaxonomyList.map((item: any, index: number) => (
                                <tr key={index}>
                                    <td>{item?.taxonomyType?.name}</td>
                                    <td>
                                        {item?.parents?.length > 0 ? item.parents.map((itm: any, index: number) => (
                                            <span key={index}>
                                                <span className='align-middle wk-size-2 mr-5'>{itm?.name}</span>
                                                {index < item.parents.length - 1 ?
                                                    <span className='mr-5'>{' > '}</span>
                                                : null}
                                            </span>
                                        )) : <span> &#8212; </span>}
                                    </td>
                                    <td>{item.name}</td>
                                    <td>{item.code}</td>
                                    {(isEditPrivilege || isDeletePrivilege) && (
                                        <td>
                                            {isEditPrivilege && <span className="wk-icon-pencil mr-10 cursor" title="Edit"
                                                onClick={() => addEdit(item)}></span>}
                                            {isDeletePrivilege && <span className="wk-icon-trash cursor" title="Delete" onClick={() => handleDelete(item)}></span>}
                                        </td>
                                    )}
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={(isEditPrivilege || isDeletePrivilege) ? 5 : 4} className="text-center" style={{ height: '7rem' }}>
                                        No record found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {isOpenAddEdit && <AddTaxonomy data={{ isOpenAddEdit, setIsOpenAddEdit, selectedItem, setIsLoad, projectList }} />}
            {isOpenDeleteConfirm && <DeleteConfirm data={{ isOpenDeleteConfirm, setIsOpenDeleteConfirm, deleteItem, message: `Are you sure you want to delete?` }} />}
        </div>
    );
};

export default TaxonomyTab;